/* ============
 * Routes File
 * ============
 *
 * The routes and redirects are defined in this file.
 */

import Full from '@/containers/Full';
import Simple from '@/containers/Simple';
import SimpleWithHeader from '@/containers/SimpleWithHeader';
import AccountContainer from '../containers/AccountContainer';
import CheckoutV2Container from '../containers/CheckoutV2Container';

/**
 * The routes
 *
 * @type {object} The routes
 */

export default [
  // APP
  {
    path: '/redirect-to-app',
    name: 'redirect.app.index',
    component: () => import('@/pages/auth/redirect-to-app'),

    // If the user needs to be a guest to view this page
    meta: {
      auth: true,

    },
  },
  {
    path: '/',
    redirect: '/job',
    name: 'Home',
    meta: { bodyClass: 'header-fixed sidebar-fixed aside-menu-fixed aside-menu-hidden' },
    component: Full,
    children: [
      // Home
      {
        path: '/home',
        name: 'home.index',
        redirect: '/job',
      },
      // missions
      {
        path: '/job',
        name: 'job.index',
        component: () => import('@/pages/jobs/index'),
        props: true,
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          bodyClass: 'no-scrollbar',
        },
      },
      // mobile edit job view
      {
        path: '/job/edit/:id',
        name: 'job.index.mobile',
        component: () => import('@/pages/jobs/job-edit'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: false,
          bodyClass: 'no-scrollbar',
        },
      },

      // Validations
      {
        path: '/validations',
        name: 'validations.index',
        component: () => import('@/pages/validations/index'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          perm: 'customerJobsList',
        },
      },

      // Contrats (SP-13434) — onglet activé par client via la permission customerContracts
      {
        path: '/contrats',
        name: 'contrats.index',
        component: () => import('@/pages/contrats/index'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          perm: 'customerContracts',
          requirePerm: true,
        },
      },
      // My Students
      {
        path: '/students',
        name: 'students.index',
        component: () => import('@/pages/students/index'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          perm: 'customerJobsList',
        },
      },
      // My invoices
      {
        path: '/invoice',
        name: 'invoice.index',
        component: () => import('@/pages/invoice/index'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          perm: 'customerBillingInvoices',
          // SP-13795 — hidden for temp agency companies (invoicing handled elsewhere)
          blockTempAgency: true,
        },
      },
      // Data
      {
        path: '/data',
        name: 'data.index',
        component: () => import('@/pages/data/v2'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
        },
      },
      {
        path: '/data/v2',
        name: 'data.v2',
        component: () => import('@/pages/data/v2'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
        },
      },
      // Quotes
      {
        path: '/quotes',
        name: 'quote.index',
        component: () => import('@/pages/quotes/index'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          perm: 'customerUserQuotesList',
        },
      },
      {
        path: '/quotes/to-validate',
        name: 'quote.to-validate',
        component: () => import('@/pages/quotes/index'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          perm: 'customerQuotesList',
        },
      },
    ],
  },
  // page valited of rejected quote send by mail
  {
    path: '/quotes/:status',
    name: 'quote.status',
    component: () => import('@/pages/quotes/status'),
    // If the user needs to be authenticated to view this page
    meta: {
      auth: false,
    },
  },
  // page notation send by mail
  {
    path: '/job/feedback/:id/:token/:fromUserId',
    name: 'job.feedback',
    component: () => import('@/pages/jobs/feedback'),
    meta: {
      auth: false,
    },
  },
  // Login
  {
    path: '/login',
    name: 'login.index',
    component: () => import('@/pages/auth/login'),

    // If the user needs to be a guest to view this page
    meta: {
      guest: true,
    },
  },
  {
    path: '/auth/:token',
    name: 'auth.token',
    component: () => import('@/pages/auth/token'),
    meta: {
      auth: false,
      bodyClass: 'auth-token',
    },
  },
  // auth0
  {
    path: '/auth0',
    name: 'auth.auth0',
    component: () => import('@/pages/auth/auth0'),
    meta: {
      auth: false,
      bodyClass: 'auth-sso',
    },
  },
  {
    path: '/account',
    name: 'account',
    component: AccountContainer,
    meta: { bodyClass: 'app-simple app-me app-create' },
    children: [
      {
        path: '/account/new',
        name: 'account.new',
        component: () => import('@/pages/account/new'),
        meta: {
          guest: true,
        },
      },
      {
        path: '/account/company',
        name: 'account.company',
        component: () => import('@/pages/account/company'),
        meta: {
          guest: true,
        },
      }, {
        path: '/account/subcompany',
        name: 'account.subcompany',
        component: () => import('@/pages/account/subcompany'),
        meta: {
          auth: true,
        },
      },
    ],
  },

  // Forgot password
  {
    path: '/password/forgot',
    name: 'password.forgot',
    component: () => import('@/pages/auth/forgot'),

    // If the user needs to be a guest to view this page
    meta: {
      guest: true,
    },
  },
  // Reset password
  {
    path: '/password/reset/:token',
    name: 'password.reset',
    component: () => import('@/pages/auth/login'),

    // If the user needs to be a guest to view this page
    meta: {
      guest: true,
    },
  },
  {
    path: '/team',
    name: 'Team',
    component: Simple,
    meta: { bodyClass: 'app-simple' },
    redirect: '/job',
    children: [
      {
        path: '/team/register',
        name: 'team.register',
        component: () => import('@/pages/account/new'),
        meta: {
          auth: false,
          bodyClass: 'team-register',
          guest: true,
        },
      },
    ],
  },
  {
    path: '/me',
    name: 'Me',
    component: SimpleWithHeader,
    meta: { bodyClass: 'header-fixed aside-menu-hidden app-simple app-me' },
    children: [
      {
        path: '/me/settings',
        name: 'me.settings',
        component: () => import('@/pages/account/settings/information'),
        meta: {
          auth: true,
          bodyClass: 'team-register',
        },
      },
      {
        path: '/me/settings/team',
        name: 'team.index',
        component: () => import('@/pages/team/index'),
        props: true,
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
        },
      },
    ],
  },

  // CHECKOUT V2

  {
    path: '/co',
    name: 'Checkout',
    component: CheckoutV2Container,
    meta: { bodyClass: 'v2-checkout' },
    children: [ // Checkout
      {
        path: '/nouvelle-mission',
        name: 'checkout.index.mobile',
        component: () => import('@/pages/checkout/template'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: false,
          bodyClass: 'checkout-address',
        },
      },
      // chooseTeam
      {
        path: '/chooseteam',
        name: 'co.v2.chooseteam',
        component: () => import('@/pages/checkout/chooseTeam/chooseTeam'),
        meta: {
          auth: true,
          bodyClass: 'checkout-template sidebar-fixed aside-menu-fixed aside-menu-hidden',
        },
      },
      // Template
      {
        path: '/template',
        name: 'co.v2.template',
        component: () => import('@/pages/checkout/template'),
        meta: {
          auth: true,
          bodyClass: 'checkout-template sidebar-fixed aside-menu-fixed aside-menu-hidden',
        },
      },
      {
        path: '/iban',
        name: 'iban',
        component: () => import('@/pages/checkout/iban'),
        meta: {
          auth: false,
          bodyClass: 'checkout-2v-index page-iban aside-menu-fixed aside-menu-hidden',
        },
      },
      {
        path: '/checkout/v2/create',
        name: 'co.v2.create',
        component: () => import('@/pages/checkout/v2/index'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          bodyClass: 'checkout-2v-index aside-menu-fixed aside-menu-hidden',
        },
      },
      {
        path: '/checkout/v2/place-and-slots/:boost?',
        name: 'co.v2.place',
        component: () => import('@/pages/checkout/v2/place-and-slots'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          bodyClass: 'checkout-2v-index aside-menu-fixed aside-menu-hidden',
        },
      },
      {
        path: '/checkout/v2/summary',
        name: 'co.v2.summary',
        component: () => import('@/pages/checkout/v2/summary'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          bodyClass: 'checkout-2v-index checkout-v2-summary aside-menu-fixed aside-menu-hidden',
        },
      },
      {
        path: '/checkout/v2/success',
        name: 'co.v2.success',
        component: () => import('@/pages/checkout/v2/success'),
        // If the user needs to be authenticated to view this page
        meta: {
          auth: true,
          bodyClass: 'checkout-2v-index aside-menu-fixed aside-menu-hidden checkout-success-index',
        },
      },
    ],
  },

  {
    path: '/',
    redirect: '/template',
  },
  // Route sans composant : elle existe pour soustraire `/job/feedback/…` au
  // catch-all ci-dessous, qui redirige tout le reste vers `/template`.
  { path: '/job/feedback/:pathMatch(.*)*' },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/template',
  },
];
